import { Component, OnDestroy, OnInit, Input, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { Ticket } from '../shared/models';
import { NotificationService, TicketService } from '../shared/services';
import { StorageService } from '../shared/services/storage.service';
import { PositionUtilService } from '../shared/utils/position-util.service';

@Component({
  selector: 'app-ticket-list',
  templateUrl: './ticket-list.component.html',
  styleUrls: ['./ticket-list.component.scss']
})
export class TicketListComponent implements OnInit, OnDestroy {

  date = new Date().toUTCString();
  @Input() tickets: Ticket[];
  eventNames = [
    'TicketCheckin',
    'PrevisionChanged',
    'TicketCalled',
    'TicketStart',
    'TicketComplete',
    'TicketCancel',
    'TransferredTicket',
    'ServiceConfigTicketCalled',
    'ServiceConfigTicketCancel'
  ];
  subscription: Subscription;
  private connectionSubscription?: Subscription;
  terminalKey: string;

  constructor(
    private storageService: StorageService,
    private router: Router,
    private route: ActivatedRoute,
    private notificationService: NotificationService,
    private ticketService: TicketService,
    private translateService: TranslateService,
    private positionUtil: PositionUtilService
  ) { }

  ngOnInit(): void {
    this.terminalKey = this.route.snapshot.paramMap.get('terminalKey');
    this.tickets = this.contextTickets();
    this.addTicketsToChannels();
    this.subscribe();

    // Eventos perdidos durante uma queda de conexão não são reentregues:
    // ressincroniza as posições uma vez ao voltar.
    this.connectionSubscription = this.notificationService.reconnections
      .subscribe(() => this.resyncPositions());
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.connectionSubscription?.unsubscribe();
  }

  /** Refaz a posição de todos os bilhetes do contexto (usa o cache do F1b). */
  private async resyncPositions(): Promise<void> {
    for (const ticket of this.contextTickets()) {
      await this.applyPosition(ticket);
      this.storageService.updateTicket(ticket);
    }
    this.tickets = this.contextTickets();
  }

  addTicketsToChannels(): void {
    this.tickets?.forEach(ticket => {
      if (ticket) {
        this.notificationService.add(ticket.id, ticket.serviceConfigId, this.eventNames);
      }
    });
  }

  get currentLang(): string {
    return this.translateService.currentLang;
  }

  get hasNoTickets(): boolean {
    return this.contextTickets().length === 0;
  }

  private contextTickets(): Ticket[] {
    return this.terminalKey
      ? this.storageService.getTicketsForTerminal(this.terminalKey)
      : this.storageService.getTicketsForCurrentContext();
  }

  async subscribe(): Promise<void> {
    this.subscription = this.notificationService.notifications.subscribe(async (response) => {

      if (response.eventName === 'PrevisionChanged') {
        this.storageService.updateTicket(response.ticket);
      }

      if (response.eventName === 'ServiceConfigTicketCancel') {
        for (let ticket of this.contextTickets()) {
          if (StorageService.isSameContext(ticket, response.ticket)) {
            await this.applyPosition(ticket);
            this.storageService.updateTicket(ticket);
          }
        }
      }

      if (response.eventName === 'TicketCalled') {
        let ticket = response.ticket;
        await this.applyPosition(ticket);
        this.storageService.updateTicket(ticket);
      }

      if (response.eventName === 'ServiceConfigTicketCalled') {
        for (let ticket of this.contextTickets()) {
          if (StorageService.isSameContext(ticket, response.ticket)) {
            await this.applyPosition(ticket);
            this.storageService.updateTicket(ticket);
          }
        }
      }

      if (response.eventName === 'TicketComplete' || response.eventName === 'TicketCancel') {
        this.storageService.removeTicket(response.ticket.id);
        this.notificationService.remove(response.ticket.id, response.ticket.serviceConfigId);
      }

      if (response.eventName === 'TransferredTicket') {
        if (this.terminalKey && !response.ticket.terminalAccessKey) {
          this.tickets.forEach(parent => this.ticketService.getChildrenKey(parent.id).subscribe({
            next: async (childKey) => {
              if (childKey === response.ticket.accessKey) {
                response.ticket.terminalAccessKey = this.terminalKey;
                await this.applyPosition(response.ticket);
                this.storageService.addTicket(response.ticket);
                this.tickets = this.contextTickets();
              }
            },
            error: (error) => console.error(`[ticket-list] falha ao buscar a chave filha do bilhete ${parent.id}`, error)
          }));
        } else if (!this.terminalKey || response.ticket.terminalAccessKey === this.terminalKey) {
          await this.applyPosition(response.ticket);
          this.storageService.addTicket(response.ticket);
        }
      }

      this.tickets = this.contextTickets();
    });
  }

  /**
   * Aplica a posição atual do bilhete sem deixar uma falha de rede
   * interromper o processamento dos demais bilhetes do mesmo evento.
   */
  private async applyPosition(ticket: Ticket): Promise<void> {
    try {
      ticket.position = await this.positionUtil.getNewPosition(ticket);
    } catch (error) {
      console.error(`[ticket-list] falha ao atualizar posição do bilhete ${ticket.id}`, error);
    }
  }

  goHome() {
    this.router.navigate(['/home']);
  }
}
